import React, { useEffect, useMemo, useRef } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { ZodTypeAny } from 'zod';
import { X, Loader2 } from 'lucide-react';
import { useCreateVendor } from '@api/hooks/usePendingDocuments';
import { useGetErpConnections } from '@api/hooks/useErpConnection';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { useVendorFieldsStore } from '@/store/vendorFieldsStore';
import { useOnboardingConfig } from '@/api/hooks/useOnboarding';
import {
  buildVendorTaxIdRows,
  buildVendorTaxIdsFromRows,
  isMaskedSensitiveValue,
  normalizeTaxIdEntries,
  VENDOR_TAX_ID_OPTIONS,
} from '@/lib/vendorTaxIds';
import type { CreateVendorRequest, DocumentFieldsResponse, TaxIdEntry } from '@schemas/api';
import type { VendorFieldConfig } from '@/store/vendorFieldsStore';

interface VendorCreationFormProps {
  initialVendorName: string;
  documentData?: DocumentFieldsResponse;
  extractionData?: Record<string, any>;
  missingReferenceId?: string;  // Public reference of the missing reference
  onSuccess?: () => void;
  onClose: () => void;
}

type VendorCreationDefaults = Partial<
  Pick<
    CreateVendorRequest,
    | 'legal_name'
    | 'company_name'
    | 'print_on_check_name'
    | 'bank_account'
    | 'default_payment_terms'
    | 'email'
    | 'phone'
    | 'mobile'
    | 'fax'
    | 'alternate_phone'
    | 'website'
    | 'address'
    | 'bill_addr'
    | 'currency_preference'
    | 'tax_ids'
    | 'status'
  >
>;

// Fallback default vendor fields if onboarding config not loaded
const DEFAULT_VENDOR_FIELDS: VendorFieldConfig[] = [
  { name: 'legal_name', label: 'Vendor Name', required: true, type: 'text' },
  { name: 'company_name', label: 'Company Name', required: false, type: 'text' },
  { name: 'email', label: 'Email', required: false, type: 'email' },
  { name: 'phone', label: 'Phone', required: false, type: 'text' },
  { name: 'website', label: 'Website', required: false, type: 'text' },
  { name: 'address', label: 'Address', required: false, type: 'text' },
  { name: 'default_payment_terms', label: 'Default Payment Terms', required: false, type: 'text' },
  { name: 'currency_preference', label: 'Currency', required: false, type: 'text' },
  { name: 'tax_ids', label: 'Tax ID', required: false, type: 'text' },
  { name: 'status', label: 'Status', required: false, type: 'text' },
];

const VENDOR_STATUS_OPTIONS = [
  { id: '', label: 'Select status' },
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
  { id: 'blocked', label: 'Blocked' },
];

const getExtractedValue = (data: Record<string, any> | undefined, key: string): any => {
  const value = data?.[key];

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return undefined;
    }
    if (value[0] && typeof value[0] === 'object' && 'value' in value[0]) {
      return value[0].value;
    }
    return value[0];
  }

  return value;
};

const getFirstExtractedValue = (data: Record<string, any> | undefined, keys: string[]): any => {
  for (const key of keys) {
    const value = getExtractedValue(data, key);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return undefined;
};

const firstString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return undefined;
};

const normalizeAddressText = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const normalized = value.replace(/\s*\n+\s*/g, ', ').trim();
    return normalized || undefined;
  }

  const normalizedObj = normalizeAddress(value);
  return formatAddress(normalizedObj);
};

const normalizeTaxIds = (taxIds: unknown): TaxIdEntry[] | undefined => {
  const entries = normalizeTaxIdEntries(taxIds as any);
  return entries.length > 0 ? entries : undefined;
};

const normalizeAddress = (address: unknown): Record<string, string> | undefined => {
  if (!address || typeof address !== 'object' || Array.isArray(address)) {
    return undefined;
  }

  const normalized = Object.fromEntries(
    Object.entries(address as Record<string, unknown>)
      .map(([key, value]) => [key.trim(), firstString(value)] as const)
      .filter((entry): entry is readonly [string, string] => Boolean(entry[0] && entry[1]))
  );

  return Object.keys(normalized).length > 0 ? normalized : undefined;
};

const formatAddress = (
  address: Record<string, string> | undefined,
  separator: string = ', '
): string | undefined => {
  if (!address) {
    return undefined;
  }

  const line1 = firstString(address.line1, address.line_1, address.address1, address.street, address.street1);
  const line2 = firstString(address.line2, address.line_2, address.address2, address.street2);
  const city = firstString(address.city, address.town);
  const region = firstString(address.state, address.province, address.region);
  const postalCode = firstString(address.postal_code, address.postalCode, address.zip, address.zip_code);
  const country = firstString(address.country, address.country_code);

  const orderedParts = [
    line1,
    line2,
    [city, region, postalCode].filter(Boolean).join(', '),
    country,
  ].filter(Boolean);

  if (orderedParts.length === 0) {
    const fallbackParts = Object.values(address)
      .map((value) => firstString(value))
      .filter((value): value is string => Boolean(value));
    return fallbackParts.length > 0 ? fallbackParts.join(separator) : undefined;
  }

  return orderedParts.length > 0 ? orderedParts.join(separator) : undefined;
};

const buildExtractedVendorDefaults = ({
  initialVendorName,
  documentData,
  extractionData,
}: Pick<VendorCreationFormProps, 'initialVendorName' | 'documentData' | 'extractionData'>): VendorCreationDefaults => {
  const legalName = firstString(
    documentData?.vendor_name_extracted,
    documentData?.vendor_name,
    getExtractedValue(extractionData, 'vendor_name'),
    initialVendorName
  );
  const taxIds =
    normalizeTaxIds(documentData?.tax_ids) ||
    normalizeTaxIds(getExtractedValue(extractionData, 'tax_ids')) ||
    normalizeTaxIds({
      CA_QST: getExtractedValue(extractionData, 'vendor_qst_number'),
      CA_GST: getExtractedValue(extractionData, 'vendor_gst_number'),
    });
  
  // Primary source: structured extracted vendor address from documentData.
  const extractedVendorAddressDict = normalizeAddress(documentData?.vendor_address_extracted);
  const extractedVendorAddressText = normalizeAddressText(documentData?.vendor_address_extracted);

  const extractedBillingAddress = getFirstExtractedValue(extractionData, [
    'vendor_billing_address',
    'billing_address',
    'bill_addr',
    'vendor_address',
  ]);
  const extractedContactAddress = getFirstExtractedValue(extractionData, [
    'vendor_contact_address',
    'contact_address',
    'address',
    'vendor_address',
  ]);

  const billingAddressFallback = normalizeAddress(
    documentData?.vendor_billing_address ||
    documentData?.billing_address ||
    documentData?.bill_addr ||
    documentData?.vendor_address ||
    extractedBillingAddress
  );
  const contactAddressFallback = normalizeAddress(
    documentData?.vendor_contact_address ||
    documentData?.contact_address ||
    documentData?.address ||
    extractedContactAddress
  );

  const billingAddress = extractedVendorAddressDict || billingAddressFallback || contactAddressFallback;
  const contactAddressText =
    extractedVendorAddressText ||
    normalizeAddressText(contactAddressFallback) ||
    normalizeAddressText(extractedContactAddress) ||
    normalizeAddressText(billingAddressFallback) ||
    normalizeAddressText(extractedBillingAddress);

  return {
    legal_name: legalName,
    company_name: legalName,
    default_payment_terms: firstString(documentData?.payment_terms, getExtractedValue(extractionData, 'payment_terms')),
    email: firstString(getExtractedValue(extractionData, 'vendor_email'), getExtractedValue(extractionData, 'email')),
    phone: firstString(getExtractedValue(extractionData, 'vendor_phone'), getExtractedValue(extractionData, 'phone')),
    mobile: firstString(getExtractedValue(extractionData, 'vendor_mobile'), getExtractedValue(extractionData, 'mobile')),
    fax: firstString(getExtractedValue(extractionData, 'vendor_fax'), getExtractedValue(extractionData, 'fax')),
    website: firstString(getExtractedValue(extractionData, 'vendor_website'), getExtractedValue(extractionData, 'website')),
    address: contactAddressText,
    bill_addr: billingAddress,
    currency_preference: firstString(documentData?.currency, getExtractedValue(extractionData, 'currency')),
    tax_ids: taxIds,
    status: 'active',
  };
};

export const VendorCreationForm: React.FC<VendorCreationFormProps> = ({
  initialVendorName,
  documentData,
  extractionData,
  missingReferenceId,
  onSuccess,
  onClose,
}) => {
  // Load onboarding config and vendor field store
  useOnboardingConfig();  // Triggers setFields in store
  const { fields: configuredFields } = useVendorFieldsStore();
  const createVendor = useCreateVendor();
  const { data: erpConnections = [] } = useGetErpConnections();
  const activeErpConnections = useMemo(
    () => erpConnections.filter((connection) => connection.status === 'active'),
    [erpConnections],
  );
  const extractedDefaults = useMemo(
    () => buildExtractedVendorDefaults({ initialVendorName, documentData, extractionData }),
    [documentData, extractionData, initialVendorName]
  );

  // Use configured fields or fallback to defaults
  let vendorFields = configuredFields.length > 0 ? configuredFields : DEFAULT_VENDOR_FIELDS;
  vendorFields = vendorFields.reduce<VendorFieldConfig[]>((acc, field) => {
    const normalizedName = field.name === 'tax_id' ? 'tax_ids' : field.name;
    if (acc.some((existing) => existing.name === normalizedName)) {
      return acc;
    }
    acc.push({
      ...field,
      name: normalizedName,
      label: normalizedName === 'tax_ids' ? 'Tax ID' : field.label,
    });
    return acc;
  }, []);

  // Ensure legal_name field is always present (required by backend)
  const hasLegalNameField = vendorFields.some(f => f.name === 'legal_name');
  if (!hasLegalNameField) {
    // Fallback: always include legal_name in form even if not in config
    vendorFields = [
      { name: 'legal_name', label: 'Vendor Name', required: true, type: 'text' },
      ...vendorFields
    ];
  }

  // Generate schema dynamically based on configured fields
  const vendorSchema = useMemo(() => {
    const schemaObj: Record<string, ZodTypeAny> = {};

    vendorFields.forEach((field) => {
      if (field.name === 'tax_ids') {
        return;
      }

      let fieldSchema: ZodTypeAny;

      switch (field.type) {
        case 'email':
          fieldSchema = z.string().trim().email(`${field.label} must be a valid email`);
          break;
        default:
          fieldSchema = z.string().trim();
      }

      if (field.required) {
        fieldSchema = (fieldSchema as z.ZodString).min(1, `${field.label} is required`);
      } else {
        // Email fields: also accept empty string (HTML inputs submit "" not undefined when blank)
        fieldSchema = field.type === 'email'
          ? z.union([z.literal(''), fieldSchema as z.ZodString]).optional()
          : (fieldSchema as z.ZodString).optional();
      }

      schemaObj[field.name] = fieldSchema;
    });

    schemaObj.tax_id_rows = z
      .array(
        z.object({
          type: z.string().optional(),
          value: z.string().optional(),
        })
      )
      .optional();
    schemaObj.erp_connection_id = activeErpConnections.length > 0
      ? z.string().min(1, 'Choose the ERP company for this vendor')
      : z.string().optional();

    return z.object(schemaObj).superRefine((data, ctx) => {
      const taxIdRows = (data.tax_id_rows || []) as Array<{ type?: string; value?: string }>;
      taxIdRows.forEach((row, index) => {
        const hasType = Boolean(row.type?.trim());
        const hasValue = Boolean(row.value?.trim());
        if (hasType !== hasValue) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['tax_id_rows', index, hasType ? 'value' : 'type'],
            message: 'Provide both a tax ID type and value',
          });
        }
      });
    });
  }, [activeErpConnections.length, vendorFields]);

  type VendorFormData = z.infer<typeof vendorSchema>;

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<VendorFormData>({
    resolver: zodResolver(vendorSchema),
    defaultValues: (() => {
      const defaults: Record<string, unknown> = {
        legal_name: extractedDefaults.legal_name || initialVendorName,
        erp_connection_id: '',
      };

      // Set sensible defaults for known fields
      vendorFields.forEach((field) => {
        const extractedValue = extractedDefaults[field.name as keyof VendorCreationDefaults];
        const normalizedExtractedValue =
          typeof extractedValue === 'string'
            ? extractedValue.trim()
            : (
              field.name === 'bill_addr' ||
              field.name === 'billing_address' ||
              field.name === 'contact_address'
            )
              ? normalizeAddressText(extractedValue)
              : undefined;
        if (normalizedExtractedValue && !defaults[field.name]) {
          defaults[field.name] = normalizedExtractedValue;
        }
        if (field.name === 'default_payment_terms' && !defaults[field.name]) {
          defaults[field.name] = 'Net 30';
        }
        if (field.name === 'status' && !defaults[field.name]) {
          defaults[field.name] = 'active';
        }
      });
      defaults.tax_id_rows = buildVendorTaxIdRows(extractedDefaults.tax_ids);

      return defaults as VendorFormData;
    })(),
  });
  const { fields: taxIdFields, append: appendTaxIdRow, remove: removeTaxIdRow } = useFieldArray({
    control,
    name: 'tax_id_rows',
  });
  const lastAppliedDefaultsRef = useRef<string>('');

  useEffect(() => {
    const nextDefaults = (() => {
      const defaults: Record<string, unknown> = {
        legal_name: extractedDefaults.legal_name || initialVendorName,
        erp_connection_id: '',
      };

      vendorFields.forEach((field) => {
        const extractedValue = extractedDefaults[field.name as keyof VendorCreationDefaults];
        const normalizedExtractedValue =
          typeof extractedValue === 'string'
            ? extractedValue.trim()
            : (
              field.name === 'bill_addr' ||
              field.name === 'billing_address' ||
              field.name === 'contact_address'
            )
              ? normalizeAddressText(extractedValue)
              : undefined;
        if (normalizedExtractedValue && !defaults[field.name]) {
          defaults[field.name] = normalizedExtractedValue;
        }
        if (field.name === 'default_payment_terms' && !defaults[field.name]) {
          defaults[field.name] = 'Net 30';
        }
        if (field.name === 'status' && !defaults[field.name]) {
          defaults[field.name] = 'active';
        }
      });
      defaults.tax_id_rows = buildVendorTaxIdRows(extractedDefaults.tax_ids);

      return defaults as VendorFormData;
    })();

    const serializedDefaults = JSON.stringify(nextDefaults);
    if (serializedDefaults === lastAppliedDefaultsRef.current) {
      return;
    }
    if (isDirty) {
      return;
    }

    reset(nextDefaults);
    lastAppliedDefaultsRef.current = serializedDefaults;
  }, [
    extractedDefaults,
    initialVendorName,
    isDirty,
    reset,
    vendorFields,
  ]);

  const onSubmit = async (data: VendorFormData) => {
    const request: CreateVendorRequest = {
      erp_connection_id: data.erp_connection_id ? String(data.erp_connection_id) : undefined,
      legal_name: String(data.legal_name || ''),
      default_payment_terms: data.default_payment_terms ? String(data.default_payment_terms) : undefined,
      tax_ids: vendorFields.some((field) => field.name === 'tax_ids')
        ? buildVendorTaxIdsFromRows(data.tax_id_rows as Array<{ type?: string; value?: string }> | undefined)
        : undefined,
      status: data.status ? String(data.status) : undefined,
    };

    // Pass through additional vendor fields
    const extraFields = [
      'company_name', 'print_on_check_name', 'bank_account', 'email',
      'phone', 'mobile', 'fax', 'alternate_phone', 'website', 'address',
      'currency_preference',
    ] as const;
    for (const field of extraFields) {
      const value = data[field as keyof VendorFormData];
      if (field === 'bank_account' && isMaskedSensitiveValue(String(value || ''))) {
        continue;
      }
      if (value && String(value).trim()) {
        (request as unknown as Record<string, unknown>)[field] = String(value);
      }
    }
    if (!request.address && extractedDefaults.address) {
      request.address = extractedDefaults.address;
    }
    if (!request.bill_addr && extractedDefaults.bill_addr) {
      request.bill_addr = extractedDefaults.bill_addr;
    }
    if (
      extractedDefaults.bill_addr &&
      data.address &&
      String(data.address).trim() === extractedDefaults.address
    ) {
      request.bill_addr = extractedDefaults.bill_addr;
    }
    try {
      await createVendor.mutateAsync(request);
      if (onSuccess) {
        setTimeout(onSuccess, 500);
      }
    } catch {
      // The mutation hook owns user-facing error notifications.
    }
  };

  const isLoading = createVendor.isPending;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Create Vendor</h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {activeErpConnections.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ERP company</label>
              <Controller
                name="erp_connection_id"
                control={control}
                render={({ field }) => (
                  <SearchableSelect
                    value={field.value || ''}
                    onChange={field.onChange}
                    disabled={isLoading}
                    options={[
                      { id: '', label: 'Choose ERP company' },
                      ...activeErpConnections.map((connection) => ({
                        id: connection.id,
                        label: connection.display_name || connection.external_company_name || `${connection.erp_type} ${connection.realm_id}`,
                      })),
                    ]}
                    placeholder="Choose ERP company"
                    clearable={false}
                  />
                )}
              />
              {errors.erp_connection_id?.message && (
                <p className="mt-1 text-sm text-red-600">{String(errors.erp_connection_id.message)}</p>
              )}
            </div>
          )}
          {vendorFields.map((field) => {
            // Get error for this field
            const fieldError = (
              field.name === 'tax_ids'
                ? undefined
                : errors[field.name as keyof VendorFormData]?.message
            ) as string;

            const taxIdRowErrors =
              errors.tax_id_rows as
                | Array<{ type?: { message?: string }; value?: { message?: string } }>
                | undefined;

            return (
              <div key={field.name}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.label}
                  {field.required && <span aria-label="required" className="text-red-600 ml-1">*</span>}
                </label>

                {field.type === 'email' ? (
                  <input
                    type="email"
                    {...register(field.name as keyof VendorFormData)}
                    aria-required={field.required}
                    aria-describedby={fieldError ? `${field.name}-error` : undefined}
                    disabled={isLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                  />
                ) : field.name === 'tax_ids' ? (
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => appendTaxIdRow({ type: 'CA_QST', value: '' })}
                      disabled={isLoading}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:bg-gray-50 disabled:text-gray-500"
                    >
                      Add Tax ID
                    </button>
                    {taxIdFields.map((taxIdField, index) => (
                      <div key={taxIdField.id} className="grid gap-2 sm:grid-cols-[minmax(0,160px)_minmax(0,1fr)_auto]">
                        <Controller
                          name={`tax_id_rows.${index}.type` as const}
                          control={control}
                          render={({ field: taxIdField }) => (
                            <SearchableSelect
                              value={taxIdField.value || ''}
                              onChange={taxIdField.onChange}
                              disabled={isLoading}
                              options={[
                                { id: '', label: 'Select tax ID type' },
                                ...VENDOR_TAX_ID_OPTIONS.map((option) => ({ id: option.value, label: option.label })),
                              ]}
                              placeholder="Select tax ID type"
                              clearable={false}
                            />
                          )}
                        />
                        <input
                          type="text"
                          {...register(`tax_id_rows.${index}.value` as const)}
                          disabled={isLoading}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                          placeholder="Enter tax ID number"
                        />
                        <button
                          type="button"
                          onClick={() => removeTaxIdRow(index)}
                          disabled={isLoading || taxIdFields.length === 1}
                          className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:bg-gray-50 disabled:text-gray-500"
                        >
                          Remove
                        </button>
                        {(taxIdRowErrors?.[index]?.type?.message || taxIdRowErrors?.[index]?.value?.message) && (
                          <p id={`${field.name}-error`} className="sm:col-span-2 mt-1 text-sm text-red-600">
                            {taxIdRowErrors?.[index]?.type?.message || taxIdRowErrors?.[index]?.value?.message}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : field.type === 'select' ? (
                  <Controller
                    name={field.name as keyof VendorFormData}
                    control={control}
                    render={({ field: selectField }) => (
                      <SearchableSelect
                        value={String(selectField.value || '')}
                        onChange={selectField.onChange}
                        aria-required={field.required}
                        aria-describedby={fieldError ? `${field.name}-error` : undefined}
                        disabled={isLoading}
                        options={field.name === 'status' ? VENDOR_STATUS_OPTIONS : [{ id: '', label: `Select ${field.label.toLowerCase()}` }]}
                        placeholder={`Select ${field.label.toLowerCase()}`}
                        clearable={false}
                      />
                    )}
                  />
                ) : (
                  <input
                    type="text"
                    {...register(field.name as keyof VendorFormData)}
                    aria-required={field.required}
                    aria-describedby={fieldError ? `${field.name}-error` : undefined}
                    disabled={isLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                  />
                )}

                {fieldError && (
                  <p id={`${field.name}-error`} className="mt-1 text-sm text-red-600">{fieldError}</p>
                )}
              </div>
            );
          })}

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:bg-gray-50 disabled:text-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Vendor'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
